import React, { useEffect, useState } from 'react';
import { Hash, Lock, Plus, X } from 'lucide-react';
import { channelsAPI } from '../utils/api';

interface Channel {
  id: number;
  name: string;
  visibility: string;
  my_role?: string;
  myRole?: string;
  member_count?: number;
  memberCount?: number;
  post_count?: number;
  postCount?: number;
}

interface ChannelsNavProps {
  activeTab: string;
  onSelectChannel: (id: number) => void;
  refreshKey: number;
  onChanged?: () => void;
}

const isShared = (c: Channel) => c.visibility === 'shared';

/**
 * 왼쪽 사이드바의 채널 섹션 (슬랙식). 내 채널 목록 + 채널 생성.
 * 기본 생성은 비공유(private).
 */
export default function ChannelsNav({ activeTab, onSelectChannel, refreshKey, onChanged }: ChannelsNavProps) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'shared'>('private');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await channelsAPI.list();
      setChannels(res.data.channels || []);
    } catch {
      /* 미인증 등은 조용히 무시 */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const create = async () => {
    if (!name.trim()) {
      setError('채널 이름을 입력하세요.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const res = await channelsAPI.create({ name: name.trim(), visibility });
      setShowCreate(false);
      setName('');
      setVisibility('private');
      await load();
      onChanged?.();
      if (res.data?.id) onSelectChannel(res.data.id);
    } catch (e: any) {
      setError(e?.response?.data?.error || '채널 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="border-t border-gray-200 my-2" />
      <div className="flex items-center justify-between px-3 mb-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">채널</span>
        <button
          onClick={() => { setShowCreate(true); setError(''); }}
          className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          title="채널 만들기"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {loading && channels.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-400">불러오는 중…</div>
      ) : channels.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-400">아직 채널이 없습니다.</div>
      ) : (
        <div className="space-y-0.5">
          {channels.map((c) => {
            const active = activeTab === `channel:${c.id}`;
            return (
              <button
                key={c.id}
                onClick={() => onSelectChannel(c.id)}
                className={`w-full flex items-center px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                }`}
                title={c.name}
              >
                {isShared(c) ? <Hash className="w-4 h-4 mr-2 flex-shrink-0" /> : <Lock className="w-4 h-4 mr-2 flex-shrink-0" />}
                <span className="truncate flex-1">{c.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* 채널 생성 모달 */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">새 채널 만들기</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">채널 이름</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder="예: 내 연구자료"
              maxLength={80}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 mb-4"
            />

            <div className="mb-4 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={visibility === 'private'} onChange={() => setVisibility('private')} className="mt-1" />
                <span>
                  <span className="flex items-center text-sm font-medium text-gray-800"><Lock className="w-4 h-4 mr-1" />비공유</span>
                  <span className="text-xs text-gray-500">나만 볼 수 있는 개인 채널 (기본값)</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={visibility === 'shared'} onChange={() => setVisibility('shared')} className="mt-1" />
                <span>
                  <span className="flex items-center text-sm font-medium text-gray-800"><Hash className="w-4 h-4 mr-1" />공유</span>
                  <span className="text-xs text-gray-500">초대한 멤버와 함께 쓰는 채널</span>
                </span>
              </label>
            </div>

            {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">취소</button>
              <button
                onClick={create}
                disabled={creating}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                {creating ? '만드는 중…' : '만들기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
